import type { KeybindingsService } from '.';
import { Signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { inject, injectable } from 'src/features/ioc';
import { buildStaticCommands } from 'src/services/commands/staticCommands';
import type { BindableCommand, KeyChord } from 'src/services/commands/types';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class KeybindingsServiceImpl implements KeybindingsService {
    readonly commands: readonly BindableCommand[];

    #bindings: Signal<ReadonlyMap<string, KeyChord | null>>;
    #isSettingsOpen = new Signal(false);

    constructor(@inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService']) {
        this.commands = buildStaticCommands(featureFlagService);
        this.#bindings = new Signal<ReadonlyMap<string, KeyChord | null>>(this.#seedBindings());
    }

    get bindings(): ReadonlySignal<ReadonlyMap<string, KeyChord | null>> {
        return this.#bindings;
    }

    get isSettingsOpen(): ReadonlySignal<boolean> {
        return this.#isSettingsOpen;
    }

    setBinding(commandId: string, chord: KeyChord | null): void {
        const next = new Map<string, KeyChord | null>(this.#bindings.peek());
        next.set(commandId, chord);
        this.#bindings.value = next;
    }

    resetBinding(commandId: string): void {
        const cmd = this.commands.find((c) => c.id === commandId);
        if (!cmd) return;
        const next = new Map<string, KeyChord | null>(this.#bindings.peek());
        next.set(commandId, cmd.defaultBinding ?? null);
        this.#bindings.value = next;
    }

    resetAll(): void {
        this.#bindings.value = this.#seedBindings();
    }

    openSettings(): void {
        this.#isSettingsOpen.value = true;
    }

    closeSettings(): void {
        this.#isSettingsOpen.value = false;
    }

    #seedBindings(): ReadonlyMap<string, KeyChord | null> {
        const map = new Map<string, KeyChord | null>();
        for (const cmd of this.commands) {
            map.set(cmd.id, cmd.defaultBinding ?? null);
        }
        return map;
    }

    [Symbol.dispose](): void {
        // No external resources held.
    }
}
